import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils/hashing"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {imageLike} from "#template/nodes/image/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {ImageLikeNode, NumberLike} from "#template/nodes/types"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator, InputMaterialConnection, OutputMaterialConnection} from "@cm/material-nodes"
import {createMappingNode, materialConnection} from "@cm/material-nodes"
import {IsNotUndefined} from "@cm/utils"
import {visitNone} from "#template/template-node/visit-helpers"

function applyMoveImage(
    uvConnection: InputMaterialConnection<"point">,
    horizontalOffset: number,
    verticalOffset: number,
    horizontalScale: number,
    verticalScale: number,
    rotation: number,
): OutputMaterialConnection<"point"> {
    return materialConnection(
        createMappingNode(uvConnection, [horizontalScale, verticalScale, 1], [0, 0, rotation * (Math.PI / 180)], [horizontalOffset, verticalOffset, 0]),
        "vector",
    )
}

export type MoveImageParameters = {
    name: string
    image: ImageLikeNode | null
    horizontalOffset?: NumberLike
    verticalOffset?: NumberLike
    horizontalScale?: NumberLike
    verticalScale?: NumberLike
    rotation?: NumberLike
}

const moveImageDefinition: TemplateNodeDefinition<MoveImageParameters> = {
    nodeClass: "MoveImage",
    validation: {
        parameters: z.object({
            name: z.string(),
            image: z.nullable(imageLike),
            horizontalOffset: z.optional(numberLike),
            verticalOffset: z.optional(numberLike),
            horizontalScale: z.optional(numberLike),
            verticalScale: z.optional(numberLike),
            rotation: z.optional(numberLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MoveImage extends ImageNode<MoveImageParameters> {
    public override get definition() {
        return moveImageDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MoveImageParameters> {
        if (this.parameters.image === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const {
            image: imageParam,
            horizontalOffset: horizontalOffsetParam,
            verticalOffset: verticalOffsetParam,
            horizontalScale: horizontalScaleParam,
            verticalScale: verticalScaleParam,
            rotation: rotationParam,
        } = this.parameters

        if (imageParam === null) return null

        const image = evaluator.evaluate(imageParam)
        const horizontalOffset = IsNotUndefined(horizontalOffsetParam) ? evaluator.evaluate(horizontalOffsetParam) : 0
        const verticalOffset = IsNotUndefined(verticalOffsetParam) ? evaluator.evaluate(verticalOffsetParam) : 0
        const horizontalScale = IsNotUndefined(horizontalScaleParam) ? evaluator.evaluate(horizontalScaleParam) : 1
        const verticalScale = IsNotUndefined(verticalScaleParam) ? evaluator.evaluate(verticalScaleParam) : 1
        const rotation = IsNotUndefined(rotationParam) ? evaluator.evaluate(rotationParam) : 0

        return scope.pureLambda<[ImageGenerator | null, number | null, number | null, number | null, number | null, number | null], ImageGenerator | null>(
            scope.tuple(image, horizontalOffset, verticalOffset, horizontalScale, verticalScale, rotation),
            ([image, horizontalOffset, verticalOffset, horizontalScale, verticalScale, rotation]) => {
                if (
                    image === null ||
                    horizontalOffset === null ||
                    verticalOffset === null ||
                    horizontalScale === null ||
                    verticalScale === null ||
                    rotation === null
                )
                    return null

                return {
                    imageNode: {
                        generator: (generatorData) => {
                            const movedUV = applyMoveImage(generatorData.uv, horizontalOffset, verticalOffset, horizontalScale, verticalScale, rotation)
                            return image.imageNode.generator({
                                ...generatorData,
                                uv: movedUV,
                            })
                        },
                        hash: hashObject({
                            type: "MoveImage",
                            inputImage: image.imageNode.hash,
                            horizontalOffset,
                            verticalOffset,
                            horizontalScale,
                            verticalScale,
                            rotation,
                        }),
                    },
                    metadata: image.metadata,
                }
            },
            "moveImage",
        )
    }
}
